import { cn } from "@/lib/utils";
import { Lock, Coins, Bot, Zap, ArrowRight, ExternalLink, CreditCard, Link, Banknote, Network, Shield } from "lucide-react";

const x402Features = [
  {
    icon: <Lock className="w-5 h-5 text-white" />,
    text: "No API key required — pay per request",
  },
  {
    icon: <Coins className="w-5 h-5 text-white" />,
    text: "USDC payments on Base — ~$0.00025 tx fees",
  },
  {
    icon: <Bot className="w-5 h-5 text-white" />,
    text: "Works with any x402-compatible AI agent",
  },
  {
    icon: <Zap className="w-5 h-5 text-white" />,
    text: "Same fast edge responses",
  },
];

const codeExample = `// AI agent request — no API key needed
const response = await fetch(
  'https://api.weatherapi.dev/now',
  { params: { lat: 37.77, lon: -122.41 } }
)

// Gets 402 Payment Required
// Agent signs USDC payment automatically
// Retries with payment proof header

// ✓ Weather data returned
// ✓ $0.001 paid in USDC
// ✓ Zero key management`;

interface X402SectionProps {
  className?: string;
}

export function X402Section({ className }: X402SectionProps) {
  return (
    <section
      id="x402"
      className={cn("py-20 px-4 sm:px-6 lg:px-8", className)}
    >
      <div className="max-w-7xl mx-auto">
        {/* Dark card container */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-800 to-slate-900 p-8 md:p-12">
          {/* Background glow effect */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-sky-500 rounded-full opacity-10 blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500 rounded-full opacity-10 blur-3xl translate-y-1/2 -translate-x-1/2" />

          <div className="relative grid lg:grid-cols-2 gap-12 items-center">
            {/* Text content */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Native{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-purple-400">
                  x402
                </span>{" "}
                support
              </h2>

              <p className="text-lg text-slate-300 mb-8">
                The first weather API with built-in HTTP 402 micropayments. Let
                AI agents autonomously access weather data without
                pre-provisioned API keys.
              </p>

              {/* Feature list */}
              <div className="grid sm:grid-cols-2 gap-4">
                {x402Features.map((feature, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/10"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-sky-500/20 to-purple-500/20 rounded-lg text-xl">
                      {feature.icon}
                    </div>
                    <span className="text-sm text-slate-300">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="/docs/x402"
                  className="px-6 py-3 bg-gradient-to-r from-sky-500 to-sky-600 text-white font-semibold rounded-xl shadow-lg shadow-sky-500/25 hover:shadow-xl hover:shadow-sky-500/30 hover:-translate-y-0.5 transition-all flex items-center gap-2"
                >
                  Learn about x402 <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="https://x402.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-white/10 text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-colors flex items-center gap-2"
                >
                  x402 Protocol <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Code window */}
            <div className="relative">
              <div className="bg-slate-950 rounded-2xl shadow-2xl overflow-hidden border border-slate-700/50">
                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-3 bg-slate-800/50 border-b border-slate-700/50">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-500/80" />
                    <span className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <span className="w-3 h-3 rounded-full bg-green-500/80" />
                  </div>
                  <span className="text-sm text-slate-400 font-mono">
                    agent.ts
                  </span>
                </div>

                {/* Code content */}
                <div className="p-4 overflow-x-auto">
                  <pre className="text-sm leading-relaxed">
                    <code className="text-slate-300 whitespace-pre">
                      {codeExample}
                    </code>
                  </pre>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-semibold rounded-xl shadow-lg flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Coinbase x402
              </div>
            </div>
          </div>

          {/* Bottom stats/logos */}
          <div className="relative mt-12 pt-8 border-t border-white/10">
            <div className="flex flex-wrap items-center justify-center gap-8 text-slate-400">
              <div className="flex items-center gap-2">
                <Link className="w-6 h-6 text-slate-500" />
                <div>
                  <p className="text-white font-semibold">100M+</p>
                  <p className="text-xs">x402 payments processed</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Banknote className="w-6 h-6 text-slate-500" />
                <div>
                  <p className="text-white font-semibold">$600M+</p>
                  <p className="text-xs">Annualized volume</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Network className="w-6 h-6 text-slate-500" />
                <div>
                  <p className="text-white font-semibold">Base</p>
                  <p className="text-xs">Low-cost L2 network</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-6 h-6 text-slate-500" />
                <div>
                  <p className="text-white font-semibold">USDC</p>
                  <p className="text-xs">Stable payments</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default X402Section;
